import { Button } from "@/components/ui/button";
import { Link, useNavigate, useParams } from "react-router-dom";
import Heading from "@/ui/Heading";
import { useEffect, useState } from "react";
import { DataTable } from "@/components/ui/datatable";
import { getOutlineColumns } from "@/features/proposal/outline/OutlineColumns";
import InfoTooltip from "@/ui/InfoTooltip";
import { useStreamProposal } from "@/features/proposal/useStreamProposal";
import EmptyTableCTA from "@/ui/EmptyTableCTA";
import { useGetStreamingStatus } from "@/features/proposal/editor/useGetStreamingStatus";
import { ChevronDown, Eye } from "lucide-react";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { useGetProposal } from "@/features/proposal/useGetProposal";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useGetOutline } from "@/stores/ProposalStore";
import { OutlineFilePicker } from "@/features/proposal/outline/OutlineFilePicker";
import { useIsMutating } from "@tanstack/react-query";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PiMagicWandDuotone } from "react-icons/pi";

const steps = [
  {
    title: "Outline Generation",
    content: "Click to start AI outline generation",
    target: "#generate-outline",
    placement: "bottom-start",
    disableBeacon: true,
  },
  {
    title: "Outline",
    content:
      "The generated outline will appear here and can be modified by you",
    target: "#generate-table",
    placement: "bottom-start",
    disableBeacon: true,
  },
  {
    title: "Edit/Add/Delete",
    content: "You can add, edit, or delete sections in the outline",
    target: "#generate-table-actions-header",
    placement: "top-end",
    disableBeacon: true,
  },
  {
    title: "Add",
    content:
      "To add a section, click + in 'Actions', then enter a title and number",
    target: "#generate-table-actions-header",
    placement: "bottom-end",
    disableBeacon: true,
  },
  {
    title: "Proposal Generation",
    content: "Click to start generating the proposal",
    target: "#generate-proposal",
    placement: "top-start",
    disableBeacon: true,
  },
];

function CreateOutline({
  streamingStatus,
  proposal,
  proposal_id,
  searchType,
  pursuitId,
}) {
  const navigate = useNavigate();

  const messages = {
    default: {
      message: "Step 1 of 2: Start by generating the outline.",
      subMessage:
        "Choose the files you want to include in this proposal then click the generate outline button below.",
    },
    pursuit: {
      message: "Step 1 of 2: Start by generating the outline.",
      subMessage: (
        <span>
          <Link
            to={`/journey-hub/${pursuitId}/documents`}
            className="text-blue-600 hover:underline"
          >
            The journey's documents
          </Link>{" "}
          will be used to write this proposal. Select your most relevant
          business documents to tailor the proposal to your business.
        </span>
      ),
    },
    proposal: {
      message: (
        <span>
          Proposal already generated. Creating a new one will{" "}
          <span className="text-destructive">overwrite</span> the existing one.
        </span>
      ),
      subMessage:
        "You already have a generated proposal for this opportunity. If you continue, you will lose your previous work.",
    },
  };
  if (streamingStatus?.status) {
    return (
      <EmptyTableCTA
        message={
          <div className="flex flex-col gap-2">
            <span class>Currently writing section:</span>
            <AnimatedShinyText
              className={
                "text-xl text-sky-700/70 via-sky-950 dark:text-sky-300/70 dark:via-white"
              }
            >
              {streamingStatus?.current_section}
            </AnimatedShinyText>
          </div>
        }
        subMessage="We are working on generating this proposal for you. You can generate a new outline once we are done."
        buttonLabel="View Progress"
        onButtonClick={() =>
          navigate(`/contract/${searchType}/${proposal_id}/generate/editor`)
        }
        Icon={Eye}
      />
    );
  }
  const msgKey = pursuitId ? "pursuit" : proposal ? "proposal" : "default";
  return (
    <div className="rounded-lg border bg-background shadow-md relative w-full flex flex-col gap-4 items-center p-6">
      <div className="flex items-center justify-center p-6 max-w-md">
        <div className="w-full transition-all">
          <h2 className="text-2xl font-semibold text-foreground mb-4 text-center">
            {messages[msgKey].message}
          </h2>
          <p className="text-center text-sm text-muted-foreground">
            {messages[msgKey].subMessage}
          </p>
        </div>
      </div>

      <OutlineFilePicker
        proposal_id={proposal_id}
        searchType={searchType}
        pursuitId={pursuitId}
      />
    </div>
  );
}

function GenerateProposalButton({
  searchType,
  proposal_id,
  tableData,
  proposal,
  isGeneratingOutline,
  isProposalLoading,
}) {
  const { mutate: startStreaming } = useStreamProposal(searchType, proposal_id);
  const navigate = useNavigate();

  async function generateProposal() {
    startStreaming();
    navigate(`/contract/${searchType}/${proposal_id}/generate/editor`);
  }
  return proposal ? (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button
            id="generate-proposal"
            className="w-50 self-end"
            variant="secondary"
            disabled={!tableData || isGeneratingOutline || isProposalLoading}
          >
            <span>Regenerate Proposal</span>
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Overwrite your existing proposal?</DialogTitle>
            <DialogDescription>
              You already have a generated proposal for this opportunity.
              Continuing will replace your existing work and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </DialogClose>

            <DialogClose asChild>
              <Button type="submit" onClick={generateProposal}>
                <PiMagicWandDuotone />
                Generate
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Button
        onClick={() =>
          navigate(`/contract/${searchType}/${proposal_id}/generate/editor`)
        }
      >
        <Eye />
        View
      </Button>
    </>
  ) : (
    <Button
      id="generate-proposal"
      className="w-50 self-end"
      onClick={generateProposal}
      disabled={!tableData || isGeneratingOutline || isProposalLoading}
    >
      <span>Generate Proposal</span>
    </Button>
  );
}

function GeneratedOutline({
  tableData,
  proposal_id,
  searchType,
  proposal,
  isProposalLoading,
  pursuitId,
}) {
  const [open, setOpen] = useState(false);
  const isGeneratingOutline = useIsMutating({
    mutationKey: ["generate-outline"],
  });

  useEffect(() => {
    if (isGeneratingOutline) {
      setOpen(false);
    }
  }, [isGeneratingOutline]);

  return (
    <div className="w-full">
      <div className="flex justify-end mt-4 gap-4">
        <Collapsible open={open} onOpenChange={setOpen} className="w-full">
          <div className="flex justify-end mt-4 gap-4">
            <CollapsibleTrigger asChild>
              <Button
                id="generate-outline"
                className="w-50 self-end transition-all [&[data-state=open]>svg]:rotate-180"
                variant="secondary"
                disabled={!tableData || isGeneratingOutline}
              >
                Regenerate Outline
                <ChevronDown className="" />
              </Button>
            </CollapsibleTrigger>
            <GenerateProposalButton
              searchType={searchType}
              proposal_id={proposal_id}
              tableData={tableData}
              proposal={proposal}
              isGeneratingOutline={isGeneratingOutline}
              isProposalLoading={isProposalLoading}
            />
          </div>
          <CollapsibleContent className="w-full flex flex-col gap-4 items-center p-6">
            <OutlineFilePicker
              searchType={searchType}
              proposal_id={proposal_id}
              pursuitId={pursuitId}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
      <h2 className="text-2xl font-semibold mb-6">Generated Outline</h2>
      <DataTable
        columns={getOutlineColumns({ proposal_id, searchType })}
        data={tableData}
        disablePagination={true}
        isLoading={isGeneratingOutline}
        id="generate-table"
      />
    </div>
  );
}

export function Outline({ proposal_id, searchType, pursuitId }) {
  const { data: streamingStatus, refetch: refetchStreamingStatus } =
    useGetStreamingStatus(proposal_id, searchType);
  const { data: proposal, isLoading: isProposalLoading } = useGetProposal(
    searchType,
    proposal_id
  );
  useEffect(() => {
    refetchStreamingStatus();
  }, []);

  const outline = useGetOutline(searchType, proposal_id);
  const tableData = outline?.main_sections.map((section) => ({
    ...section,
    subRows: section.sub_sections,
  }));
  const isCTA = !tableData?.length || streamingStatus?.status;
  if (isCTA) {
    return (
      <div className="p-20">
        <CreateOutline
          streamingStatus={streamingStatus}
          proposal={proposal}
          proposal_id={proposal_id}
          searchType={searchType}
          pursuitId={pursuitId}
        />
      </div>
    );
  }
  return (
    <div>
      <GeneratedOutline
        tableData={tableData}
        proposal_id={proposal_id}
        searchType={searchType}
        proposal={proposal}
        isProposalLoading={isProposalLoading}
        pursuitId={pursuitId}
      />
    </div>
  );
}

function OutlineGenerator() {
  const { proposal_id, searchType } = useParams();

  return (
    <div className="flex flex-col">
      <div className="p-4">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Heading as="h1">Generate outline</Heading>
          <InfoTooltip description="Click on 'Generate outline' to start outline generation." />
        </div>
      </div>
      <Outline proposal_id={proposal_id} searchType={searchType} />
    </div>
  );
}

export default OutlineGenerator;
